import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const whyChooseUsJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Why Choose SafeStay Solutions - Beverly MA Accessibility Experts',
    description:
      'Licensed & insured home accessibility contractors in Beverly, MA. Fast installation, quality guaranteed. Serving North Shore families since 2021.',
    url: `${siteUrl}/why-choose-us`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Why Choose Us', item: `${siteUrl}/why-choose-us` },
      ],
    },
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'SafeStay Solutions',
      description: 'Specialized home accessibility and safety construction company serving Beverly, MA and the North Shore.',
      foundingDate: '2021',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Beverly',
        addressRegion: 'MA',
        addressCountry: 'US',
      },
      telephone: '+16175396650',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '2500',
      },
    },
  },
];

export default function WhyChooseUsPage() {
  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: 'Licensed & Insured',
      description:
        'Fully licensed contractors with comprehensive insurance coverage. Our team meets all Massachusetts state requirements and maintains the highest industry certifications for home accessibility modifications and safety installations.',
      details: [
        'Massachusetts state licensed contractors',
        'Comprehensive liability insurance',
        'Workers compensation coverage',
        'Industry-certified installation specialists',
        'Regular safety training and updates',
      ],
    },
    {
      icon: 'ri-time-line',
      title: 'Fast & Clean Installation',
      description:
        'Professional safety upgrades installed in days, with many projects completed in just twenty‑four hours. Our streamlined process ensures your home is reinforced quickly without compromising quality. We treat your home with respect, protecting floors and furniture, and leaving every workspace spotless.',
      details: [
        'Most projects completed within 24‑48 hours',
        'Flexible scheduling to fit your needs',
        'Complete workspace protection and cleanup',
        'Minimal disruption to daily routines',
        'Efficient project management',
        'Same‑day emergency services available',
      ],
    },
    {
      icon: 'ri-award-line',
      title: 'Quality Guaranteed by Experts',
      description: `Every installation is backed by our comprehensive warranty and satisfaction guarantee. Our team brings years of specialized experience in aging‑in‑place modifications and ADA compliance. We use premium materials and proven techniques developed through we've helped North Shore families with successful installations, ensuring your modifications last for years while maintaining safety and functionality.`,
      details: [
        'Specialized expertise in accessibility modifications',
        'Years of experience with aging‑in‑place solutions',
        'Comprehensive workmanship warranty',
        '100% satisfaction guarantee',
        'Premium‑grade materials only',
        'ADA‑compliant installations',
        'Post‑installation support and follow‑up',
      ],
    },
  ];

  const additionalReasons = [
    {
      icon: 'ri-user-heart-line',
      title: 'Family-Focused Approach',
      description:
        'We understand the emotional aspect of aging in place. Our team works closely with families to create solutions that preserve independence while ensuring safety.',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Local North Shore Experts',
      description:
        'Based in Beverly, MA, we serve the North Shore community with pride. We understand local building codes and the unique needs of New England homes.',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Compassionate Service',
      description:
        'Our team treats every home like it belongs to our own family. We work with respect, care, and understanding of the sensitive nature of home modifications.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Comprehensive Solutions',
      description:
        'From simple grab bar installations to complete bathroom renovations, we handle projects of all sizes with the same level of professionalism and attention to detail.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Why Choose SafeStay Solutions - Beverly MA Accessibility Experts"
        description="Licensed & insured home accessibility contractors serving Beverly, MA. Fast 24-48hr installation, quality guaranteed, ADA-compliant modifications. Trusted by North Shore families since 2021."
        keywords="why choose SafeStay Solutions, licensed accessibility contractor Beverly MA, aging in place experts, ADA compliant modifications North Shore"
        canonical={`${siteUrl}/why-choose-us`}
        ogUrl={`${siteUrl}/why-choose-us`}
        jsonLd={whyChooseUsJsonLd}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2B4A] via-[#2A3B5A] to-[#1A2B4A]"></div>
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-[#D4AF37]/20 backdrop-blur-sm px-4 sm:px-6 py-2 rounded-full mb-6 sm:mb-8">
            <span className="text-[#D4AF37] text-xs sm:text-sm font-semibold tracking-wider">
              THE SAFESTAY DIFFERENCE
            </span>
          </div>

          <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight">
            Why Families Trust<br />
            SafeStay Solutions
          </h1>

          <p className="text-white/90 text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
            Since 2021, we've helped North Shore families create safer, more accessible homes for their
            loved ones. Here's what sets us apart.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/free-assessment"
              className="bg-[#D4AF37] text-[#1A2B4A] px-8 py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#C4A137] transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              Schedule Free Assessment
            </Link>
            <a
              href="tel:6175396650"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border-2 border-white/30"
            >
              Call (617) 539-6650
            </a>
          </div>
        </div>
      </section>

      {/* Main Benefits Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h2 className="text-[#1A2B4A] text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Our Core Commitments
            </h2>
            <p className="text-gray-600 text-base sm:text-lg md:text-xl max-w-3xl mx-auto">
              Three pillars that define every project we undertake
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#FAF9F7] rounded-2xl p-8 lg:p-10 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-[#D4AF37]/30"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mb-6 sm:mb-8">
                  <i className={`${benefit.icon} text-[#1A2B4A] text-3xl sm:text-4xl`}></i>
                </div>

                <h3 className="text-[#1A2B4A] text-2xl sm:text-3xl font-bold mb-4 sm:mb-5">
                  {benefit.title}
                </h3>

                <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                  {benefit.description}
                </p>

                <div className="space-y-3">
                  {benefit.details.map((detail, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700 text-sm sm:text-base">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Reasons Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h2 className="text-[#1A2B4A] text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              More Reasons to Choose Us
            </h2>
            <p className="text-gray-600 text-base sm:text-lg md:text-xl max-w-3xl mx-auto">
              What makes SafeStay Solutions the preferred choice for North Shore families
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {additionalReasons.map((reason, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 sm:p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${reason.icon} text-[#D4AF37] text-2xl sm:text-3xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-[#1A2B4A] text-xl sm:text-2xl font-bold mb-3">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-[#1A2B4A] text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">
            Ready to Make Your Home Safer?
          </h2>
          <p className="text-gray-600 text-lg sm:text-xl md:text-2xl mb-8 sm:mb-12 leading-relaxed">
            Schedule your free home safety assessment today. No obligation, just expert advice tailored to your
            family's needs.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/free-assessment"
              className="bg-[#D4AF37] text-[#1A2B4A] px-8 py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#C4A137] transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              Get Your Free Assessment
            </Link>
            <a
              href="tel:6175396650"
              className="bg-[#1A2B4A] text-white px-8 py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
            >
              Call (617) 539-6650
            </a>
          </div>

          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#D4AF37] text-xl"></i>
              <span className="text-sm sm:text-base">Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-[#D4AF37] text-xl"></i>
              <span className="text-sm sm:text-base">Fast Installation</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-award-line text-[#D4AF37] text-xl"></i>
              <span className="text-sm sm:text-base">Quality Guaranteed</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}